import React, { useEffect, useRef, useState } from "react";
import { Star, Quote } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { WhatsAppIcon } from "./icons/WhatsAppIcon";
import { motion, useInView } from "framer-motion";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from "@/components/ui/carousel";
const testimonialCategories = [
  {
    title: "Competência",
    description:
      "O escritório é sério, preparado, tecnicamente seguro e conhecedor das peculiaridades das causas dos servidores públicos. Isso quem corrobora são nossos próprios clientes:",
    type: "images",
    images: [
      "https://assets.cdn.filesafe.space/vyxGSs9NtFYsqksgNt9c/media/6a31615ddd7879239a2f03a7.webp",
      "https://assets.cdn.filesafe.space/vyxGSs9NtFYsqksgNt9c/media/6a31607e6dd61c546ac57080.webp",
      "https://assets.cdn.filesafe.space/vyxGSs9NtFYsqksgNt9c/media/6a3161df22582c14f1226c03.webp",
      "https://assets.cdn.filesafe.space/vyxGSs9NtFYsqksgNt9c/media/6a3160d722582c14f1224f4e.webp",
    ],
    reviews: [],
  },
  {
    title: "Atendimento e Acolhimento",
    description:
      "O nosso atendimento é atencioso, cordial, humano e respeitoso. Com explicações claras, respostas objetivas e atualizações sobre o andamento do processo.",
    type: "images",
    images: [
      "https://assets.cdn.filesafe.space/vyxGSs9NtFYsqksgNt9c/media/6a316af40a683b64fe810b0a.webp",
      "https://assets.cdn.filesafe.space/vyxGSs9NtFYsqksgNt9c/media/6a31669ea20786fa2d77f1ab.webp",
      "https://assets.cdn.filesafe.space/vyxGSs9NtFYsqksgNt9c/media/6a316832dd7879239a3024d4.webp",
      "https://assets.cdn.filesafe.space/vyxGSs9NtFYsqksgNt9c/media/6a3160ffc87bd94b121d232b.webp",
    ],
    reviews: [],
  },
  {
    title: "Transparência e Confiança",
    description:
      "Escritório honesto, com alta credibilidade, com prestação de contas, lisura e segurança no trato com valores e informações sensíveis.",
    type: "images",
    images: [
      "https://assets.cdn.filesafe.space/vyxGSs9NtFYsqksgNt9c/media/6a3161be6dd61c546ac599c1.webp",
      "https://assets.cdn.filesafe.space/vyxGSs9NtFYsqksgNt9c/media/6a31670e6dd61c546ac62c15.webp",
      "https://assets.cdn.filesafe.space/vyxGSs9NtFYsqksgNt9c/media/6a316205602476018d5226e4.webp",
      "https://assets.cdn.filesafe.space/vyxGSs9NtFYsqksgNt9c/media/6a316731602476018d52b820.webp",
    ],
    reviews: [],
  },
  {
    title: "Reconhecimento",
    description:
      "Nosso escritório possui uma atuação tão rápida e precisa, que até mesmo os servidores dos tribunais notaram e elogiaram:",
    type: "images",
    images: [
      "https://assets.cdn.filesafe.space/vyxGSs9NtFYsqksgNt9c/media/6a3313a06ff9a379cdd78215.webp",
      "https://assets.cdn.filesafe.space/vyxGSs9NtFYsqksgNt9c/media/6a427b346f9cd8f3509c458e.webp",
      "https://assets.cdn.filesafe.space/vyxGSs9NtFYsqksgNt9c/media/6a427b3415c172b5cfcf3f51.webp",
      "https://assets.cdn.filesafe.space/vyxGSs9NtFYsqksgNt9c/media/6a427b3415c172b5cfcf3f56.webp",
    ],
    reviews: [],
  },
];

interface TestimonialsProps {
  customButton?: {
    text: string;
    onClick: () => void;
  };
}

const Testimonials = ({ customButton }: TestimonialsProps = {}) => {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const widgetRef = useRef<HTMLDivElement>(null);
  const widgetInView = useInView(widgetRef, { once: true, amount: 0.1 });

  useEffect(() => {
    if (!api) {
      return;
    }

    setCurrent(api.selectedScrollSnap());

    api.on("select", () => {
      setCurrent(api.selectedScrollSnap());
    });
  }, [api]);

  useEffect(() => {
    if (!widgetInView) return;
    const script = document.createElement("script");
    script.src =
      "https://reputationhub.site/reputation/assets/review-widget.js";
    script.async = true;
    document.body.appendChild(script);
    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, [widgetInView]);

  return (
    <section id="depoimentos" className="py-24 bg-muted/20 overflow-hidden">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7 }}
          className="text-center mb-20 space-y-6"
        >
          <h2 className="text-3xl md:text-[48px] lg:text-[48px] font-bold text-primary font-serif pl-5 pr-5">
            O que nossos clientes dizem sobre nós?
          </h2>

          <div className="flex flex-col items-center gap-4">
            <img
              src="https://storage.googleapis.com/revex-reputation-production/assets/google-icon.svg"
              alt="Google"
              className="h-12 w-auto"
              loading="lazy"
              decoding="async"
            />
            <div className="flex text-yellow-400 gap-1">
              {[...Array(5)].map((_, i) => (
                <motion.div
                  key={i}
                  whileHover={{ scale: 1.3, rotate: 15 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <Star className="w-6 h-6 fill-current" />
                </motion.div>
              ))}
            </div>
            <p className="font-bold text-primary/80 text-base pl-5 pr-5">
              Nota MÁXIMA no Google com base em +300 avaliações!
            </p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="relative max-w-7xl mx-auto px-2 md:px-4 lg:px-8"
        >
          <Carousel
            setApi={setApi}
            opts={{ align: "start", loop: true }}
            className="w-full"
          >
            <CarouselContent>
              {testimonialCategories.map((category, idx) => (
                <CarouselItem key={idx}>
                  <div className="space-y-12 pt-[60px] pb-10 px-0 md:px-2 lg:px-4">
                    <div className="text-center max-w-3xl mx-auto space-y-4 px-12 md:px-12 lg:px-0">
                      <motion.div
                        initial={{ opacity: 0, scale: 0.5 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: 0.5 }}
                        className="flex justify-center gap-2 mb-4"
                      >
                        {testimonialCategories.map((_, i) => (
                          <button
                            key={i}
                            className={`h-1.5 transition-all duration-300 rounded-full ${i === current ? "w-8 bg-accent" : "w-4 bg-muted-foreground/30 hover:bg-muted-foreground/50"}`}
                            onClick={() => api?.scrollTo(i)}
                            aria-label={`Ir para a categoria ${i + 1}`}
                          />
                        ))}
                      </motion.div>
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: 0.6 }}
                        className="inline-block px-6 py-2 bg-[#192847] text-white font-bold rounded-full uppercase tracking-widest text-lg"
                      >
                        {category.title}
                      </motion.div>
                      <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: 0.7 }}
                        className="text-base md:text-xl text-muted-foreground leading-relaxed px-2 md:px-0 text-justify md:text-center"
                      >
                        {category.description}
                      </motion.p>
                    </div>

                    {category.type === "images" && category.images ? (
                      <div
                        className={`grid grid-cols-1 ${category.images.length === 1 ? "max-w-3xl" : "md:grid-cols-2 max-w-full lg:max-w-4xl"} gap-6 md:gap-10 mx-auto`}
                      >
                        {category.images.map((img, iIdx) => (
                          <motion.div
                            key={iIdx}
                            initial={{ opacity: 0, scale: 0.9 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true, margin: "-50px" }}
                            transition={{ duration: 0.5, delay: iIdx * 0.1 }}
                            className="w-full aspect-[4/3] md:aspect-[3/2] rounded-[2rem] shadow-xl hover:-translate-y-2 transition-all duration-500 bg-white overflow-hidden p-2 flex items-center justify-center"
                          >
                            <img
                              src={img}
                              alt="Avaliação do cliente"
                              className="w-full h-full object-contain"
                              loading="lazy"
                              decoding="async"
                            />
                          </motion.div>
                        ))}
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
                        {category.reviews.map((review, rIdx) => (
                          <Card
                            key={rIdx}
                            className="relative border-none shadow-xl bg-white rounded-[2rem] overflow-hidden group hover:-translate-y-2 transition-all duration-500"
                          >
                            <Quote className="absolute -top-6 -right-6 w-48 h-48 text-accent/5 rotate-180 pointer-events-none" />
                            <CardContent className="relative z-10 p-10 space-y-6">
                              <Quote className="w-12 h-12 text-accent/20 group-hover:text-accent/40 transition-colors" />
                              <p className="text-lg text-muted-foreground leading-relaxed italic line-clamp-6">
                                "{review.text}"
                              </p>
                              <div className="flex items-center justify-between pt-6 border-t border-border/50">
                                <div className="flex items-center gap-4">
                                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center font-bold text-primary text-xl uppercase">
                                    {review.name[0]}
                                  </div>
                                  <div>
                                    <h4 className="font-extrabold text-primary">
                                      {review.name}
                                    </h4>
                                    <p className="text-xs text-muted-foreground uppercase tracking-widest">
                                      {review.date}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex text-yellow-400 gap-0.5">
                                  {[...Array(review.rating)].map((_, i) => (
                                    <motion.div
                                      key={i}
                                      whileHover={{ scale: 1.3, rotate: 15 }}
                                      transition={{
                                        type: "spring",
                                        stiffness: 300,
                                      }}
                                    >
                                      <Star className="w-4 h-4 fill-current" />
                                    </motion.div>
                                  ))}
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    )}
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.6 }}
              className="absolute top-[150px] md:top-[160px] lg:top-[160px] -translate-y-1/2 left-0 md:-left-4 lg:left-4 z-10"
            >
              <CarouselPrevious className="static translate-y-0 translate-x-0 top-auto left-auto flex w-10 h-10 md:w-16 md:h-16 border-2 border-[#192847] text-[#192847] hover:bg-[#192847] hover:text-white transition-colors [&>svg]:w-5 [&>svg]:h-5 md:[&>svg]:w-6 md:[&>svg]:h-6" />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.6 }}
              className="absolute top-[150px] md:top-[160px] lg:top-[160px] -translate-y-1/2 right-0 md:-right-4 lg:right-4 z-10"
            >
              <CarouselNext className="static translate-y-0 translate-x-0 top-auto right-auto flex w-10 h-10 md:w-16 md:h-16 border-2 border-[#192847] text-[#192847] hover:bg-[#192847] hover:text-white transition-colors [&>svg]:w-5 [&>svg]:h-5 md:[&>svg]:w-6 md:[&>svg]:h-6" />
            </motion.div>
          </Carousel>
        </motion.div>

        <motion.div
          ref={widgetRef}
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7, delay: 0.4 }}
          className="mt-20 max-w-6xl mx-auto w-full"
        >
          {widgetInView && (
            <iframe
              className="lc_reviews_widget"
              src="https://reputationhub.site/reputation/widgets/review_widget/vyxGSs9NtFYsqksgNt9c"
              frameBorder="0"
              scrolling="no"
              style={{ minWidth: "100%", width: "100%" }}
              title="Avaliações"
              loading="lazy"
            ></iframe>
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.5, delay: 0.6 }}
          className="mt-16 flex justify-center pb-8"
        >
          {customButton ? (
            <button
              onClick={customButton.onClick}
              className="inline-flex items-center justify-center gap-2 sm:gap-3 whitespace-normal sm:whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 w-[85%] sm:w-auto font-bold px-6 py-4 sm:px-8 sm:py-[18px] h-auto rounded-full bg-[#075E54] hover:bg-[#043b35] text-white border-2 border-white shadow-lg hover:scale-105 transition-transform uppercase text-sm sm:text-base mx-auto sm:mx-0 text-center"
            >
              <WhatsAppIcon className="w-5 h-5 sm:w-6 sm:h-6 shrink-0" />
              {customButton.text}
            </button>
          ) : (
            <button
              onClick={(e) => {
                e.preventDefault();
                window.dispatchEvent(new CustomEvent("open-home-popup"));
              }}
              className="inline-flex items-center justify-center gap-2 sm:gap-3 whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 w-[85%] sm:w-auto font-bold px-6 py-4 sm:px-8 sm:py-[18px] h-auto rounded-full bg-[#075E54] hover:bg-[#043b35] text-white border-2 border-white shadow-lg hover:scale-105 transition-transform uppercase text-sm sm:text-base mx-auto sm:mx-0"
              aria-label="Entrar em contato"
            >
              <WhatsAppIcon className="w-5 h-5 sm:w-6 sm:h-6" />
              Entrar em contato
            </button>
          )}
        </motion.div>
      </div>
    </section>
  );
};

export default Testimonials;
